/**
 * DriverManagerPanel — 드라이버 관리 내용 (모달 껍질 없음)
 *
 * ConnectionModal 의 "드라이버 관리" 탭 안에 렌더됨.
 * 이전 DriverManagerModal 의 본문 로직을 재사용하도록 분리.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiUploadCloud, FiExternalLink, FiTrash2, FiCheckCircle, FiAlertCircle, FiInfo, FiChevronRight, FiChevronDown } from 'react-icons/fi';
import { useConfirm } from './ConfirmDialog';

const getInstallGuides = (t) => ({
  oracle: {
    steps: [
      t('common:drv.oraStep1'),
      t('common:drv.oraStep2'),
      t('common:drv.oraFileExample'),
      t('common:drv.oraStep3'),
      t('common:drv.oraStep4'),
    ],
    tips: [
      t('common:drv.oraBasicNote'),
      t('common:drv.oraBundleNote'),
    ],
  },
  postgresql: {
    steps: [
      t('common:drv.pgNoInstall'),
      t('common:drv.pgStep'),
    ],
    tips: [
      t('common:drv.pgPureJs'),
      t('common:drv.tbTls'),
    ],
  },
  tibero: {
    steps: [
      t('common:drv.tbStep1'),
      t('common:drv.tbInternalNote'),
      t('common:drv.tbStep2'),
      t('common:drv.tbStep3'),
      t('common:drv.tbOtherVersion'),
    ],
    tips: [
      t('common:drv.tbZipNote'),
      t('common:drv.tbDetectOrder'),
      t('common:drv.tbOracleCompat'),
    ],
  },
});

const DriverManagerPanel = ({ apiBaseUrl }) => {
    const { t } = useTranslation();
  const confirm = useConfirm();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedGuides, setExpandedGuides] = useState({});
  const fileInputRef = useRef(null);
  const pendingDbTypeRef = useRef(null);

  const API = apiBaseUrl;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/drivers`);
      setDrivers(res.data);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUploadClick = (dbType) => {
    pendingDbTypeRef.current = dbType;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const dbType = pendingDbTypeRef.current;
    pendingDbTypeRef.current = null;
    e.target.value = '';
    if (!file || !dbType) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setResult({ dbType, success: false, message: t('common:drv.zipOnly') });
      return;
    }
    setUploading(dbType);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('driverZip', file);
      const res = await axios.post(`${API}/drivers/${dbType}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10 * 60 * 1000,
      });
      setResult({ dbType, ...res.data });
      await load();
    } catch (err) {
      setResult({ dbType, success: false, message: err.response?.data?.message || err.message });
    } finally {
      setUploading(null);
    }
  };

  const handleUninstall = async (dbType) => {
    const ok = await confirm({
      title: t('common:drv.removeDriver'),
      message: t('common:drv.removeConfirm', { dbType: dbType }),
      confirmText: t('common:drv.remove'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/drivers/${dbType}`);
      setResult({ dbType, ...res.data });
      await load();
    } catch (err) {
      setResult({ dbType, success: false, message: err.response?.data?.message || err.message });
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  };

  const statusBadge = (status) => {
    const map = {
      user:     { label: t('common:drv.userFolder'), color: '#0d6efd' },
      bundled:  { label: t('common:drv.appBundle'),    color: '#198754' },
      repo:     { label: t('common:drv.devRepo'),  color: '#6c757d' },
      npm:      { label: t('common:drv.npmBuiltin'),   color: '#8e44ad' },
      missing:  { label: t('common:drv.notInstalled'),     color: '#dc3545' },
    };
    const info = map[status] || map.missing;
    return (
      <span style={{
        padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 600,
        background: `${info.color}22`, color: info.color, border: `1px solid ${info.color}66`,
      }}>{info.label}</span>
    );
  };

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #666)' }}>
        {t('common:drv.nativeUploadable')}
        {t('common:drv.uploadLoc1')} <code>&lt;userData&gt;/drivers/</code> {t('common:drv.uploadLoc2', { bundle: t('common:drv.appBundle') })}
      </div>

      {loading && <div style={{ padding: 20, textAlign: 'center' }}>{t('common:drv.loading')}</div>}

      {!loading && drivers.map(drv => (
        <div key={drv.dbType} style={{
          border: '1px solid var(--color-border, #dee2e6)',
          borderRadius: 6,
          padding: 12,
          background: 'var(--color-bg-secondary, #f8f9fa)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{drv.name}</span>
            {statusBadge(drv.status)}
            {drv.size > 0 && <span style={{ fontSize: 11, color: '#888' }}>({formatSize(drv.size)})</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #666)', marginBottom: 6 }}>{drv.description}</div>
          {drv.location && (
            <div style={{ fontSize: 11, color: '#999', marginBottom: 6, wordBreak: 'break-all' }}>
              <code>{drv.location}</code>
            </div>
          )}
          <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
            {t('common:drv.license')} {drv.licenseNote}
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {drv.npmPackage ? (
              <span style={{ fontSize: 11, color: '#198754', display: 'flex', alignItems: 'center', gap: 4 }}>
                <FiCheckCircle size={12} /> {t('common:drv.npmProvided', { pkg: drv.npmPackage })}
              </span>
            ) : (
              <>
                <button className="conn-header-btn" onClick={() => handleUploadClick(drv.dbType)} disabled={uploading === drv.dbType}>
                  <FiUploadCloud /> {uploading === drv.dbType ? t('common:drv.uploading') : t('common:drv.pickZip')}
                </button>
                {drv.downloadUrl && (
                  <button className="conn-header-btn" onClick={() => window.open(drv.downloadUrl, '_blank', 'noopener')} title={t('common:drv.openVendorPage')}>
                    <FiExternalLink /> {t('common:drv.downloadPage')}
                  </button>
                )}
                {drv.status === 'user' && (
                  <button className="conn-header-btn" onClick={() => handleUninstall(drv.dbType)} title={t('common:drv.removeUserFolder')} style={{ color: '#dc3545', borderColor: '#dc3545' }}>
                    <FiTrash2 /> {t('common:drv.remove')}
                  </button>
                )}
              </>
            )}
          </div>

          {getInstallGuides(t)[drv.dbType] && (
            <div style={{ marginTop: 8 }}>
              <button
                className="conn-header-btn"
                style={{ fontSize: 11, padding: '2px 8px' }}
                onClick={() => setExpandedGuides(p => ({ ...p, [drv.dbType]: !p[drv.dbType] }))}
              >
                {expandedGuides[drv.dbType] ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                <FiInfo size={12} /> {t('common:drv.installGuide')}
              </button>
              {expandedGuides[drv.dbType] && (
                <div style={{
                  marginTop: 6, padding: 8, fontSize: 11,
                  background: 'var(--color-bg, #fff)',
                  border: '1px solid var(--color-border, #dee2e6)',
                  borderRadius: 4,
                  color: 'var(--color-text, #333)',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('common:drv.steps')}</div>
                  <ol style={{ paddingLeft: 18, margin: 0, marginBottom: 6 }}>
                    {getInstallGuides(t)[drv.dbType].steps.map((s, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>{s}</li>
                    ))}
                  </ol>
                  {getInstallGuides(t)[drv.dbType].tips?.length > 0 && (
                    <>
                      <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>{t('common:drv.note')}</div>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {getInstallGuides(t)[drv.dbType].tips.map((tip, i) => (
                          <li key={i} style={{ marginBottom: 2, color: 'var(--color-text-secondary, #666)' }}>{t}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {result && result.dbType === drv.dbType && (
            <div style={{
              marginTop: 8, padding: 8, borderRadius: 4, fontSize: 12,
              background: result.success ? '#d1e7dd' : '#f8d7da',
              color: result.success ? '#0a3622' : '#58151c',
              border: `1px solid ${result.success ? '#a3cfbb' : '#f1aeb5'}`,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {result.success ? <FiCheckCircle /> : <FiAlertCircle />}
              {result.message}
            </div>
          )}
        </div>
      ))}

      <input ref={fileInputRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={handleFileSelected} />
    </div>
  );
};

export default DriverManagerPanel;
